/**
 * 管理端左侧导航配置。
 *
 * 对齐原型 `prototype/admin/src/layout/menuConfig.ts`：2 顶层叶子(首页、学生与报名) + 6 分组，
 * 共 22 个菜单叶子，label/path/pageCode/icon 与原型一致（补消 ADM-33 为原型外新增，
 * 原型同步随 makeup-deduction 反向归档补齐）。
 *
 * 权限元数据（staff-rbac-wechat-login:SC-50）：
 * - `menuKeys`：后端权限矩阵的菜单键（来自 pageCode 拆分，如 ADM-02/04 -> [ADM-02, ADM-04]）。
 *   后端补全 GET /auth/permissions 返回 menuKeys 后，AdminLayout 切换到精确菜单键过滤。
 * - `positions`：岗位粗粒度可见性兜底（后端未返回 menuKeys 时使用）。
 *   undefined 表示所有岗位可见；数组表示仅这些岗位可见。参考 PRD §4.2 功能权限矩阵。
 *
 * 后端契约缺口：GET /auth/permissions 当前仅返回 positions/campusScope，
 * 不含 menuKeys。故 SC-50 的"无权限菜单隐藏"当前依赖 positions 粗粒度兜底，越权访问由后端 403 兜底。
 */
import type { ComponentType } from "react";
import {
  ApartmentOutlined,
  AuditOutlined,
  BookOutlined,
  HomeOutlined,
  MoneyCollectOutlined,
  ScheduleOutlined,
  SettingOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import type { StaffPosition } from "../api/staff.api";

export interface MenuLeaf {
  key: string;
  label: string;
  path: string;
  /** 合并表示如 ADM-02/04（一个叶子覆盖多个 ADM 编号） */
  pageCode: string;
  icon?: ComponentType;
  /** 后端权限菜单键（从 pageCode 拆分）；后端返回 menuKeys 后用于精确过滤 */
  menuKeys: string[];
  /** 岗位粗粒度可见性兜底（undefined=全部可见），参考 PRD §4.2 */
  positions?: StaffPosition[];
}

export interface MenuGroup {
  key: string;
  label: string;
  icon?: ComponentType;
  children: MenuLeaf[];
}

export type MenuNode = MenuLeaf | MenuGroup;

export function isMenuGroup(node: MenuNode): node is MenuGroup {
  return "children" in node;
}

/** 从 pageCode 拆分菜单键（处理 "ADM-02/04" 形态）。 */
function keysOf(pageCode: string): string[] {
  const [firstKey, ...suffixes] = pageCode
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (!firstKey) return [];
  const prefix = firstKey.slice(0, firstKey.lastIndexOf("-") + 1);
  return [firstKey, ...suffixes.map((suffix) => `${prefix}${suffix}`)];
}

/** 教务相关岗位集合（用于排课/消课/课程等菜单的可见性） */
const ADMIN_ACADEMIC: StaffPosition[] = ["CAMPUS_ADMIN", "ACADEMIC_AFFAIRS"];
/** 含招生顾问的学生/收费相关岗位集合 */
const ADMIN_ACADEMIC_ENROLL: StaffPosition[] = [
  "CAMPUS_ADMIN",
  "ACADEMIC_AFFAIRS",
  "ENROLLMENT_CONSULTANT",
];

export const menuTree: MenuNode[] = [
  {
    key: "home",
    label: "首页",
    path: "/",
    pageCode: "ADM-01",
    icon: HomeOutlined,
    menuKeys: keysOf("ADM-01"),
  },
  {
    key: "students",
    label: "学生与报名",
    path: "/students",
    pageCode: "ADM-02/04",
    icon: TeamOutlined,
    menuKeys: keysOf("ADM-02/04"),
    positions: ADMIN_ACADEMIC_ENROLL,
  },
  {
    key: "billing",
    label: "收费管理",
    icon: MoneyCollectOutlined,
    children: [
      {
        key: "orders",
        label: "订单与收款",
        path: "/billing/orders",
        pageCode: "ADM-09",
        menuKeys: keysOf("ADM-09"),
        positions: ADMIN_ACADEMIC_ENROLL,
      },
      {
        key: "fund-exceptions",
        label: "资金异常",
        path: "/billing/fund-exceptions",
        pageCode: "ADM-14",
        menuKeys: keysOf("ADM-14"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "revenue",
        label: "收入统计",
        path: "/billing/revenue",
        pageCode: "ADM-15",
        menuKeys: keysOf("ADM-15"),
        positions: ADMIN_ACADEMIC_ENROLL,
      },
    ],
  },
  {
    key: "classes",
    label: "班级教务",
    icon: ScheduleOutlined,
    children: [
      {
        key: "class-list",
        label: "班级管理",
        path: "/classes",
        pageCode: "ADM-16",
        menuKeys: keysOf("ADM-16"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "placement",
        label: "筛选分班",
        path: "/classes/placement",
        pageCode: "ADM-17/18",
        menuKeys: keysOf("ADM-17/18"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "schedule",
        label: "排课工作台",
        path: "/classes/schedule",
        pageCode: "ADM-19/20/21",
        menuKeys: keysOf("ADM-19/20/21"),
        positions: ADMIN_ACADEMIC,
      },
    ],
  },
  {
    key: "deduction",
    label: "消课与账本",
    icon: AuditOutlined,
    children: [
      {
        key: "deduction-records",
        label: "消课记录",
        path: "/deduction/records",
        pageCode: "ADM-22",
        menuKeys: keysOf("ADM-22"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "makeup-deductions",
        label: "补消",
        path: "/deduction/makeup",
        pageCode: "ADM-33",
        menuKeys: keysOf("ADM-33"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "balances",
        label: "剩余课时查询",
        path: "/deduction/balances",
        pageCode: "ADM-34",
        menuKeys: keysOf("ADM-34"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "deduction-exceptions",
        label: "消课异常与冲正",
        path: "/deduction/exceptions",
        pageCode: "ADM-23",
        menuKeys: keysOf("ADM-23"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "ledger",
        label: "学生课时账本",
        path: "/deduction/ledger",
        pageCode: "ADM-24",
        menuKeys: keysOf("ADM-24"),
        positions: ADMIN_ACADEMIC,
      },
    ],
  },
  {
    key: "catalog",
    label: "课程与资源",
    icon: BookOutlined,
    children: [
      {
        key: "subjects",
        label: "教学科目",
        path: "/catalog/subjects",
        pageCode: "ADM-27",
        menuKeys: keysOf("ADM-27"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "skus",
        label: "课程SKU",
        path: "/catalog/skus",
        pageCode: "ADM-05/06",
        menuKeys: keysOf("ADM-05/06"),
        positions: ADMIN_ACADEMIC,
      },
      {
        key: "products",
        label: "课程商品",
        path: "/catalog/products",
        pageCode: "ADM-07/08",
        menuKeys: keysOf("ADM-07/08"),
        positions: ADMIN_ACADEMIC_ENROLL,
      },
      {
        key: "classrooms",
        label: "教室管理",
        path: "/catalog/classrooms",
        pageCode: "ADM-28",
        menuKeys: keysOf("ADM-28"),
        positions: ADMIN_ACADEMIC,
      },
    ],
  },
  {
    key: "org",
    label: "组织与权限",
    icon: ApartmentOutlined,
    children: [
      {
        key: "staff",
        label: "员工管理",
        path: "/org/staff",
        pageCode: "ADM-25",
        menuKeys: keysOf("ADM-25"),
        positions: ["CAMPUS_ADMIN"],
      },
      {
        key: "roles",
        label: "角色权限",
        path: "/org/roles",
        pageCode: "ADM-26",
        menuKeys: keysOf("ADM-26"),
        positions: ["CAMPUS_ADMIN"],
      },
      {
        key: "campuses",
        label: "校区管理",
        path: "/org/campuses",
        pageCode: "ADM-29",
        menuKeys: keysOf("ADM-29"),
        positions: ["CAMPUS_ADMIN"],
      },
    ],
  },
  {
    key: "system",
    label: "系统运营",
    icon: SettingOutlined,
    children: [
      {
        key: "settings",
        label: "系统设置",
        path: "/system/settings",
        pageCode: "ADM-32",
        menuKeys: keysOf("ADM-32"),
        positions: ["CAMPUS_ADMIN"],
      },
      {
        key: "logs",
        label: "操作日志",
        path: "/system/logs",
        pageCode: "ADM-31",
        menuKeys: keysOf("ADM-31"),
        positions: ["CAMPUS_ADMIN"],
      },
    ],
  },
];

/** 全部菜单叶子（21 个），用于路由生成与选中态计算 */
export const allLeaves: MenuLeaf[] = menuTree.flatMap((node) =>
  isMenuGroup(node) ? node.children : [node],
);

/**
 * 判断菜单叶子对当前用户是否可见。
 *
 * 精确模式：后端返回非空 menuKeys 时仅按角色授权交集过滤（rbac-menu-key-alignment:SC-09/SC-10，
 * 不叠加岗位）；menuKeys 为空数组表示后端确认该用户无任何菜单权限，仅无岗位约束的叶子可见。
 * 兜底模式（menuKeys 为 null/undefined，即权限快照未加载或拉取失败）：按岗位粗粒度
 * positions 判定，未配置 positions 视为所有岗位可见。
 *
 * @param leaf 菜单叶子
 * @param userPositions 当前用户岗位列表（来自 GET /auth/permissions 回写）
 * @param allowedMenuKeys 后端返回的可访问菜单键（null/undefined 表示快照未知）
 */
export function isMenuVisible(
  leaf: MenuLeaf,
  userPositions: string[],
  allowedMenuKeys?: string[] | null,
): boolean {
  // 精确模式：后端返回了非空 menuKeys，按菜单键交集判定
  if (userPositions.includes("HQ_ADMIN")) return true;

  if (allowedMenuKeys && allowedMenuKeys.length > 0) {
    return leaf.menuKeys.some((key) => allowedMenuKeys.includes(key));
  }
  // 空数组 = 后端确认无任何菜单权限：仅无岗位约束的叶子（首页）可见
  if (allowedMenuKeys && allowedMenuKeys.length === 0) {
    return !leaf.positions || leaf.positions.length === 0;
  }
  // 兜底模式：按岗位粗粒度判定（未配置 positions 视为全部可见）
  if (!leaf.positions || leaf.positions.length === 0) return true;
  return leaf.positions.some((pos) => userPositions.includes(pos));
}
